// Kleine Anzeige-Helfer für die Billtoro-Seiten (nur Deutsch)
import { fmtDate } from '../lib/format';
import type { PlanId, Tarif, TarifAngebot } from './types';

// „9 €“, „7,50 €“
export function euro(v: number): string {
  const ganz = Number.isInteger(v);
  return `${v.toLocaleString('de-DE', { minimumFractionDigits: ganz ? 0 : 2, maximumFractionDigits: 2 })} €`;
}

// Ganze Tage bis zu einem Zeitpunkt (aufgerundet: noch 2 h = noch 1 Tag), 0 = vorbei
export function tageBis(iso: string, jetzt = Date.now()): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - jetzt) / 86_400_000));
}

// „noch 9 Tage“, „noch 1 Tag“, „endet heute“
export function nochTage(iso: string): string {
  const n = tageBis(iso);
  return n <= 0 ? 'endet heute' : n === 1 ? 'noch 1 Tag' : `noch ${n} Tage`;
}

export const datum = (iso: string) => fmtDate(iso);

export const angebot = (tarif: Tarif, id: PlanId): TarifAngebot | undefined => tarif.tarife.find((t) => t.id === id);

// „November 2026“ aus 'YYYY-MM'
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
export function monatText(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return `${MONATE[m - 1] ?? m} ${y}`;
}

// Erster Tag des nächsten Monats als Text („1. November“): dann zählen Rechnungen und Angebote wieder von vorn
export function naechsterMonat(jetzt = new Date()): string {
  const berlin = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit' }).format(jetzt);
  const m = Number(berlin.slice(5, 7));
  return `1. ${MONATE[m % 12]}`;
}

// 'YYYY-MM' des laufenden Monats in Berlin
export function dieserMonat(jetzt = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit' }).format(jetzt);
}

// 'YYYY-MM' der letzten n Monate (neueste zuerst), ab dem Vormonat
export function letzteMonate(n: number, jetzt = new Date()): string[] {
  const berlin = dieserMonat(jetzt);
  let y = Number(berlin.slice(0, 4));
  let m = Number(berlin.slice(5, 7));
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    m -= 1;
    if (m === 0) {
      m = 12;
      y -= 1;
    }
    out.push(`${y}-${String(m).padStart(2, '0')}`);
  }
  return out;
}

// Zeiträume fürs Steuerbüro (wie der Server, billtoro/steuerbuero-zugang.ts): 'YYYY-Qn' der letzten n Quartale und
// 'YYYY' der letzten n Jahre, jeweils mit dem laufenden, neueste zuerst
export function letzteQuartale(n: number, jetzt = new Date()): string[] {
  const berlin = dieserMonat(jetzt);
  let y = Number(berlin.slice(0, 4));
  let q = Math.ceil(Number(berlin.slice(5, 7)) / 3);
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    out.push(`${y}-Q${q}`);
    q -= 1;
    if (q === 0) {
      q = 4;
      y -= 1;
    }
  }
  return out;
}

export function letzteJahre(n: number, jetzt = new Date()): string[] {
  const y = Number(dieserMonat(jetzt).slice(0, 4));
  return Array.from({ length: n }, (_, i) => String(y - i));
}

// „September 2026“, „3. Quartal 2026“, „Jahr 2026“
export function zeitraumText(z: string): string {
  const q = /^(\d{4})-Q([1-4])$/.exec(z);
  if (q) return `${q[2]}. Quartal ${q[1]}`;
  return /^\d{4}$/.test(z) ? `Jahr ${z}` : monatText(z);
}
